import React, {
	createContext, useCallback, useContext, useEffect, useState,
} from 'react';
import {useSelector} from 'react-redux';
import {getSeconds, getTimeString} from '../../../utils/date-utils';

export const FilterContext = createContext(null);
export const useFilter = () => {
	return useContext(FilterContext);
};

const FilterContextProvider = (props) => {
	const {settings, variants} = useSelector((state) => state.filters);
	const [filterId, setFilterId] = useState(null);
	const [choices, setChoices] = useState({});
	const [draftChoices, setDraftChoices] = useState({});
	const [ranges, setRanges] = useState({});
	const [radios, setRadios] = useState({});
	const [length, setLength] = useState({min: '', max: ''});
	const [draftRanges, setDraftRanges] = useState({});
	const [draftLength, setDraftLength] = useState({min: '', max: ''});
	const [filters, setFilters] = useState(null);
	const [searches, setSearches] = useState([]);
	const [name, setName] = useState('');
	const [search, setSearch] = useState(undefined);
	const {children} = props;

	const setFilterHandler = (id) => {
		return () => {
			const next = filterId === id ? null : id;
			if (next) {
				setDraftChoices((prev) => ({
					...prev,
					[next]: Array.isArray(choices[next]) ? [...choices[next]] : [],
				}));
			}
			setFilterId(next);
		};
	};

	const applyFilter = useCallback((id) => {
		const pending = Array.isArray(draftChoices[id]) ? [...draftChoices[id]] : [];
		setChoices((prev) => ({...prev, [id]: pending}));
		setSearches((prev) => [
			...prev.filter((item) => item.type !== id),
			...pending.map((title) => ({title, type: id})),
		]);
		setFilterId(null);
	}, [draftChoices]);

	const applyAllFilters = useCallback(() => {
		setChoices((prev) => {
			const next = {...prev};
			Object.keys(draftChoices).forEach((id) => {
				next[id] = Array.isArray(draftChoices[id]) ? [...draftChoices[id]] : [];
			});
			return next;
		});

		setRanges((prev) => ({...prev, ...draftRanges}));

		if (draftLength.min || draftLength.max) {
			setLength(draftLength);
		}

		setSearches((prev) => {
			const draftIds = new Set(Object.keys(draftChoices));
			const rangeIds = new Set(Object.keys(draftRanges));
			let next = prev.filter((item) => (
				!draftIds.has(item.type)
				&& !rangeIds.has(item.type)
				&& !String(item.type).startsWith('length')
			));

			Object.entries(draftChoices).forEach(([id, values]) => {
				(values || []).forEach((title) => {
					next.push({title, type: id});
				});
			});

			Object.entries(draftRanges).forEach(([id, range]) => {
				if (range && range.length === 2) {
					next.push({
						title: `${id.toUpperCase()} ${range[0]}-${range[1]}`,
						type: id,
					});
				}
			});

			if (draftLength.min || draftLength.max) {
				const minSec = getSeconds(draftLength.min || '00:00');
				const maxSec = getSeconds(draftLength.max || '00:00');
				next.push({title: `Длина от ${minSec} сек`, type: 'length_min'});
				next.push({title: `Длина до ${maxSec} сек`, type: 'length_max'});
			}

			return next;
		});

		setFilterId(null);
	}, [draftChoices, draftLength, draftRanges]);

	const resetFilter = useCallback((id) => {
		setDraftChoices((prev) => ({...prev, [id]: []}));
		setChoices((prev) => ({...prev, [id]: []}));
		setSearches((prev) => prev.filter((item) => item.type !== id));
	}, []);

	const clearFilters = () => {
		setChoices({});
		setDraftChoices({});
		setDraftRanges({});
		setDraftLength({min: '', max: ''});
		setRanges({});
		setRadios({});
		setName('');
		setLength({min: '', max: ''});
	};

	const getFilters = useCallback(() => {
		const currentFilters = {};
		Object.keys(choices).forEach((key) => {
			if (choices[key] && choices[key].length) {
				currentFilters[key] = choices[key].sort().join('$$');
			} else {
				delete currentFilters[key];
			}
		});
		Object.keys(ranges).forEach((key) => {
			if (ranges[key].length) {
				const [min, max] = ranges[key];
				currentFilters[`${key}_min`] = min;
				currentFilters[`${key}_max`] = max;
			}
		});
		if (length.max !== '00:00') {
			let lengthMin = length.min;
			if (!lengthMin) {
				lengthMin = '00:00';
			}
			currentFilters.length_min = getSeconds(lengthMin);
			if (!!length.max) {
				currentFilters.length_max = getSeconds(length.max);
			}
		}
		return {...radios, ...currentFilters};
	}, [choices, length, radios, ranges]);

	useEffect(() => {
		if (variants) {
			const withChoices = ['checkbox', 'radio'];
			const withMinMax = ['range', 'length'];
			const filters = settings.map((setting) => {
				if (withChoices.includes(setting.type)) {
					const settingChoices = variants[setting.id] || [];
					return {...setting, choices: settingChoices};
				}
				if (withMinMax.includes(setting.type)) {
					const min = variants[`${setting.id}_min`];
					const max = variants[`${setting.id}_max`];
					if (!setting.defaultValue && setting.type === 'range') {
						return {
							...setting, min, max, defaultValue: [min, max],
						};
					}
					if (setting.type === 'length') {
						const length = {};
						length.min = getTimeString(min);
						length.max = getTimeString(max);
						return {...setting, min: length.min, max: length.max};
					}
				}
				return setting;
			});
			setFilters(filters);
		}
	}, [settings, variants]);

	return (
		<FilterContext.Provider value={{
			filterId,
			choices,
			setChoices,
			draftChoices,
			setDraftChoices,
			draftRanges,
			setDraftRanges,
			draftLength,
			setDraftLength,
			ranges,
			setRanges,
			length,
			setLength,
			radios,
			setRadios,
			filters,
			searches,
			setSearches,
			name,
			setName,
			search,
			setSearch,
			setFilterHandler,
			applyFilter,
			applyAllFilters,
			resetFilter,
			clearFilters,
			getFilters,
		}}
		>
			{children}
		</FilterContext.Provider>
	);
};

export default FilterContextProvider;
